// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";

import { DashboardPanel } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";
import { isSet } from "utils";
import { getPanelSelection, toDropdownItems } from "guiutils";
import { IRegulation } from "parameter";
import { IState } from "states/states";
import { EmobilityViewMode } from "states/cachedstates";

import Dropdown from "view/comp/Dropdown";

interface IEMobilitySelectExtProps {
  className?: string;
  dashboardIndex?: number;
}

interface IEMobilitySelectProps {
  controlParameters: IRegulation[] | undefined;
  selectedDashboardIndex: number;
  selectedEMobilityViewMode: EmobilityViewMode | undefined;
}

const mapStateToProps = (state: IState, ownProps: IEMobilitySelectExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    DashboardPanel.MONITOR,
    ownProps.dashboardIndex,
  );
  const props: IEMobilitySelectProps = {
    controlParameters: state.parameter?.regulation,
    selectedDashboardIndex: index,
    selectedEMobilityViewMode: selection?.eMobilityViewMode,
  };
  return props;
};

interface IEMobilitySelectDispatch {
  onEMoblityWidgetChanged: (dashboardIndex: number, selection: EmobilityViewMode) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEMobilitySelectDispatch = {
    onEMoblityWidgetChanged: (dashboardIndex: number, selection: EmobilityViewMode) => {
      const payload: IActionTypeSelectGuiItem = { dashboardIndex, selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_EMOBILITY_WIDGET, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_user_caption: {
    id: "Select E-Mobility info",
  },
});

class EMobilitySelectComponent extends React.Component<
  IEMobilitySelectExtProps &
    IEMobilitySelectProps &
    IEMobilitySelectDispatch &
    WrappedComponentProps
> {
  private getViewModeName(viewMode: EmobilityViewMode) {
    return viewMode === EmobilityViewMode.CHARGERLIST ? (
      <FormattedMessage id="Chargers" />
    ) : viewMode === EmobilityViewMode.RFIDLIST ? (
      <FormattedMessage id="Charging Cards" />
    ) : (
      <></>
    );
  }

  public render() {
    const eMobilityItems = toDropdownItems(
      [EmobilityViewMode.CHARGERLIST, EmobilityViewMode.RFIDLIST],
      "eMobilityId",
      (name, value) => {
        this.props.onEMoblityWidgetChanged(this.props.selectedDashboardIndex, value);
      },
      (value) => this.getViewModeName(value),
    );

    return (
      <div id="emobilityselect" className={this.props.className}>
        <Dropdown
          items={eMobilityItems}
          caption={this.props.intl.formatMessage(messages.msg_user_caption)}
          curItem={
            isSet(this.props.selectedEMobilityViewMode)
              ? this.getViewModeName(this.props.selectedEMobilityViewMode)
              : this.props.intl.formatMessage(messages.msg_user_caption)
          }
        />
      </div>
    );
  }
}

const ControlCircuitSelect = connect(mapStateToProps, mapDispatchToProps)(EMobilitySelectComponent);

export default injectIntl(ControlCircuitSelect);
